
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td } from '../components/UI';
import { Building, MapPin, Users, ChevronRight, Package, AlertCircle } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';

// Mock customer data for the supplier
const mockCustomers = [
  { 
    id: 'c1', 
    name: 'VMI Enterprise AG', 
    location: 'Zürich, CH', 
    managedSkus: 15, 
    criticalItems: 2,
    openOrders: 3
  },
  { 
    id: 'c2', 
    name: 'Global Manufacturing Ltd', 
    location: 'Geneva, CH', 
    managedSkus: 8, 
    criticalItems: 0,
    openOrders: 1
  },
  { 
    id: 'c3', 
    name: 'Alpine Logistics Hub', 
    location: 'Bern, CH', 
    managedSkus: 22, 
    criticalItems: 5,
    openOrders: 0
  }
];

const SupplierCustomers: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredData = useMemo(() => {
    return mockCustomers.filter(customer => {
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = String((customer as any)[key] || '').toLowerCase();
        return itemValue.includes((value as string).toLowerCase());
      });
    });
  }, [columnFilters]);

  return (
    <div className="p-6 space-y-8 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('my_customers')}
        breadcrumbs={[{ label: user?.companyName || 'Supplier' }, { label: t('customers') }]}
      />

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
        <Table>
          <Thead>
            <Tr isHeader>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('customer')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('name', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('managed_skus')}</span>
                  <input 
                    className="w-24 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('managedSkus', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('critical_items')}</span>
                  <input 
                    className="w-24 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('criticalItems', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('open_orders')}</span>
                  <input 
                    className="w-24 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('openOrders', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                 {t('actions')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData.map((customer) => (
              <Tr 
                key={customer.id} 
                onClick={() => navigate(`/supplier-customers/${customer.id}`)}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors group"
              >
                <Td>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-brand-light dark:bg-[#262626] rounded-md flex items-center justify-center border border-gray-200 dark:border-[#333] shrink-0">
                       <Building size={18} className="text-brand-dark dark:text-white" />
                    </div>
                    <div>
                       <h3 className="text-sm font-bold text-brand-dark dark:text-white group-hover:text-brand-medium transition-colors">{customer.name}</h3>
                       <div className="flex items-center gap-1 text-[10px] text-gray-500 font-medium mt-0.5">
                          <MapPin size={10} /> {customer.location}
                       </div>
                    </div>
                  </div>
                </Td>
                <Td className="text-right">
                  <div className="inline-flex items-center gap-2 font-bold text-gray-700 dark:text-gray-300">
                     <Package size={14} className="text-gray-400" />
                     {customer.managedSkus}
                  </div>
                </Td>
                <Td className="text-right">
                  {customer.criticalItems > 0 ? (
                    <div className="inline-flex items-center gap-2 font-bold text-red-600 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-sm border border-red-100 dark:border-red-900/30">
                       <AlertCircle size={14} />
                       {customer.criticalItems}
                    </div>
                  ) : (
                    <span className="text-gray-400 font-medium">-</span>
                  )}
                </Td>
                <Td className="text-right">
                   <span className={`font-bold ${customer.openOrders > 0 ? 'text-brand-dark dark:text-white' : 'text-gray-400'}`}>
                      {customer.openOrders}
                   </span>
                </Td>
                <Td className="text-right">
                   <ChevronRight size={18} className="text-gray-300 group-hover:text-brand-medium transition-colors" />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
};

export default SupplierCustomers;
